import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

const helpers = createMultiStyleConfigHelpers([
  'root',
  'header',
  'title',
  'selectedLabel',
  'selectedValue',
  'grid',
  'description',
  'tile',
  'tileImageArea',
  'tileImage',
  'tileImagePlaceholder',
  'tileLabelArea',
  'tileLabel',
  'tileNameLine1',
  'tileNameLine2',
  'tileDescription',
  'tileViewDetails',
  'tileStockMessage',
  'tilePriceMessage',
  'tileStockMessageOOS',
  'tileStockMessageLow',
  'tileStockBadge',
  'tileStockBadgeLow',
  'tileStockBadgeOOS',
  'tileConfigurableBadge',
  'tileOOSOverlayLine',
  'moreCta',
  'moreCtaIcon',
  'moreCtaLabel',
  'moreCtaCount',
  'moreCtaMore',
]);

/** Shared product-tag badge positioning (Figma product tags). */
const tileStockBadgeBase = {
  border: 'none',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 600,
  left: {base: '5.5px', lg: '7px'},
  top: {base: '5.5px', lg: '7px'},
  lineHeight: 1.2,
  position: 'absolute',
  px: {base: '4px', lg: '4px'},
  py: {base: '2px', lg: '4px'},
  textAlign: 'center',
  textTransform: 'none',
};

/**
 * @typedef {import('@chakra-ui/styled-system').PartsStyleInterpolation<{
 *   keys: (
 *     "root" | "header" | "title" | "selectedLabel" | "selectedValue" |
 *     "grid" | "description" | "tile" | "tileImageArea" | "tileImage" | "tileImagePlaceholder" |
 *     "tileLabelArea" | "tileLabel" | "tileNameLine1" | "tileNameLine2" | "tileDescription" |
 *     "tileViewDetails" |
 *     "tileStockMessage" | "tilePriceMessage" | "tileStockMessageOOS" | "tileStockMessageLow" |
 *     "tileStockBadge" | "tileStockBadgeLow" | "tileStockBadgeOOS" |
 *     "tileConfigurableBadge" | "tileOOSOverlayLine" |
 *     "moreCta" | "moreCtaIcon" | "moreCtaLabel" | "moreCtaCount" | "moreCtaMore"
 *   )[]
 * }>} CoverSwatchGridStyles
 */
const CoverSwatchGrid = helpers.defineMultiStyleConfig({
  baseStyle: ({isSelected, isOutOfStock}) => ({
    root: {
      mb: 6,
      mt: 4,
      w: 'full',
    },
    header: {
      alignItems: 'baseline',
      display: 'flex',
      gap: 2,
      mb: {base: '16px', lg: '16px'},
    },
    title: {
      color: 'brandColors.greyscale',
      fontSize: {base: '16px', lg: '18px'},
      fontWeight: 500,
      letterSpacing: 'normal',
      lineHeight: {base: 1.4, lg: 1.2},
      textTransform: 'none',
    },
    selectedLabel: {
      color: 'gray.500',
      fontSize: 'sm',
      ml: 1,
    },
    selectedValue: {
      color: 'gray.900',
      fontSize: 'sm',
      fontWeight: 'semibold',
    },
    grid: {
      columns: {base: 4},
      spacing: {base: '4px', lg: '5px'},
    },
    // Disclaimer note below the cover grid (FSD: "Choose your cover").
    description: {
      color: 'brandColors.greyscale',
      fontSize: {base: '14px', lg: '14px'},
      fontWeight: 400,
      lineHeight: 1.5,
      mt: {base: '16px', lg: '16px'},
      textAlign: 'left',
      w: 'full',
    },
    tile: {
      bg: 'white',
      border: '2px solid',
      borderColor: isSelected ? 'grayAlpha.90' : 'transparent',
      borderRadius: {base: '4px', lg: '3px'},
      cursor: isOutOfStock ? 'not-allowed' : 'pointer',
      display: 'flex',
      flexDirection: 'column',
      gap: {base: '14px', lg: '10px'},
      overflow: 'hidden',
      pb: {base: '8px', lg: '11.5px'},
      position: 'relative',
      w: 'full',
      ...(!isSelected && !isOutOfStock && {
        _hover: {
          borderRadius: {base: '4px', lg: '6px'},
          boxShadow: {lg: 'inset 0 0 0 1px rgba(27, 26, 24, 0.9)'},
        },
      }),
      _focusVisible: {
        outline: '2px solid',
        outlineColor: 'gray.900',
        outlineOffset: '2px',
      },
    },
    tileImageArea: {
      h: {base: '90px', lg: '91px'},
      overflow: 'hidden',
      p: {base: '2px', lg: '4px'},
      position: 'relative',
      w: 'full',
    },
    tileImage: {
      borderRadius: {base: '4px', lg: '3px'},
      h: {base: '100%', lg: '85px'},
      maxH: {base: '82px', lg: '85px'},
      objectFit: 'cover',
      w: 'full',
    },
    tileImagePlaceholder: {
      alignItems: 'center',
      bg: 'gray.100',
      borderRadius: {base: '4px', lg: '3px'},
      display: 'flex',
      h: 'full',
      justifyContent: 'center',
      w: 'full',
    },
    tileLabelArea: {
      alignItems: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      px: '8px',
      textAlign: 'center',
      w: 'full',
    },
    tileLabel: {
      color: 'black',
      fontSize: {base: '13px', lg: '12px'},
      fontWeight: 600,
      lineHeight: 1.2,
      noOfLines: 2,
    },
    tileNameLine1: {
      color: 'black',
      fontFamily: 'body',
      fontSize: {base: '13px', lg: '16px'},
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontWeight: 600,
      lineHeight: {base: 1.2, lg: 1.4},
      textAlign: 'center',
      w: 'full',
    },
    tileNameLine2: {
      color: 'black',
      fontFamily: 'body',
      fontSize: {base: '13px', lg: '12px'},
      fontWeight: 400,
      lineHeight: 1.2,
      textAlign: 'center',
      w: 'full',
    },
    tileDescription: {
      color: 'black',
      fontSize: {base: '13px', lg: '12px'},
      fontWeight: 400,
      lineHeight: 1.2,
    },
    tileViewDetails: {
      color: 'brandColors.viewDetailsLink',
      cursor: 'pointer',
      display: {base: 'block', lg: 'none'},
      fontSize: '13px',
      fontWeight: 600,
      lineHeight: 1.2,
      textAlign: 'center',
      whiteSpace: 'nowrap',
      marginLeft: {base: '-3px', lg: '0'},
      _hover: {
        color: 'brandColors.viewDetailsLink',
      },
      _focusVisible: {
        outline: '2px solid',
        outlineColor: 'brandColors.viewDetailsLink',
        outlineOffset: '2px',
      },
    },
    tileStockMessage: {
      color: 'success.dark',
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.2,
      textAlign: 'center',
      w: 'full',
    },
    tilePriceMessage: {
      color: 'greyscale.70',
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.2,
      textAlign: 'center',
      w: 'full',
    },
    tileStockMessageLow: {
      color: 'warning.dark',
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.2,
      textAlign: 'center',
      w: 'full',
    },
    tileStockMessageOOS: {
      color: 'error.standard',
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.2,
      textAlign: 'center',
      w: 'full',
    },
    tileStockBadge: {
      ...tileStockBadgeBase,
      bg: 'success.background',
      color: 'success.dark',
    },
    tileStockBadgeLow: {
      ...tileStockBadgeBase,
      bg: 'warning.background',
      color: 'warning.dark',
    },
    tileStockBadgeOOS: {
      ...tileStockBadgeBase,
      bg: 'error.background',
      color: 'error.dark',
    },
    tileConfigurableBadge: {
      ...tileStockBadgeBase,
      bg: 'gray.900',
      color: 'white',
    },
    // Sold-out diagonal (Figma 7730:61429) — ~135deg, 6px band (2px white / 2px black / 2px white).
    tileOOSOverlayLine: {
      background: 'linear-gradient(to bottom, #FFFFFF 0, #FFFFFF 2px, #000000 2px, #000000 4px, #FFFFFF 4px, #FFFFFF 6px)',
      height: '6px',
      left: '50%',
      pointerEvents: 'none',
      position: 'absolute',
      top: '50%',
      transform: 'translate(-50%, -50%) rotate(135deg)',
      transformOrigin: 'center',
      width: {base: '58px', lg: '72px'},
    },
    moreCta: {
      alignItems: 'center',
      bg: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      justifyContent: 'center',
      minH: {base: '166px', lg: '167px'},
      py: '8px',
      w: 'full',
      _focusVisible: {
        outline: '2px solid',
        outlineColor: 'gray.900',
        outlineOffset: '2px',
      },
    },
    moreCtaIcon: {
      boxSize: '32px',
      color: 'primary.darkLinen',
      flexShrink: 0,
    },
    moreCtaLabel: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'center',
      w: 'full',
    },
    moreCtaCount: {
      color: 'brandColors.primaryText90',
      fontSize: '16px',
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontWeight: 600,
      lineHeight: 1.4,
      textAlign: 'center',
      w: 'full',
    },
    moreCtaMore: {
      color: 'brandColors.primaryText90',
      fontSize: '16px',
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontWeight: 600,
      lineHeight: 1.4,
      textAlign: 'center',
      w: 'full',
    },
  }),
});

export default CoverSwatchGrid;
